import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, Shield, BarChart3, Send, Building2, User, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const LandingPage = () => {
  const navigate = useNavigate();
  const [requestForm, setRequestForm] = useState({ name: '', phone: '', email: '', society_name: '', state: '', city: '', society_address: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const indianStates = {
    'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Kurnool', 'Tirupati', 'Rajahmundry', 'Kakinada'],
    'Arunachal Pradesh': ['Itanagar', 'Naharlagun', 'Pasighat', 'Tawang'],
    'Assam': ['Guwahati', 'Silchar', 'Dibrugarh', 'Jorhat', 'Nagaon', 'Tinsukia', 'Tezpur'],
    'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Darbhanga', 'Purnia', 'Arrah'],
    'Chhattisgarh': ['Raipur', 'Bhilai', 'Bilaspur', 'Korba', 'Durg', 'Rajnandgaon'],
    'Delhi': ['New Delhi', 'North Delhi', 'South Delhi', 'East Delhi', 'West Delhi', 'Dwarka', 'Rohini', 'Saket'],
    'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda'],
    'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Gandhinagar', 'Bhavnagar', 'Junagadh', 'Anand'],
    'Haryana': ['Gurugram', 'Faridabad', 'Panipat', 'Ambala', 'Karnal', 'Hisar', 'Rohtak', 'Sonipat', 'Panchkula'],
    'Himachal Pradesh': ['Shimla', 'Dharamshala', 'Mandi', 'Solan', 'Kullu', 'Manali'],
    'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Hazaribagh', 'Deoghar'],
    'Karnataka': ['Bengaluru', 'Mysuru', 'Mangaluru', 'Hubli', 'Belgaum', 'Dharwad', 'Shimoga'],
    'Kerala': ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kannur', 'Kollam', 'Palakkad'],
    'Madhya Pradesh': ['Bhopal', 'Indore', 'Jabalpur', 'Gwalior', 'Ujjain', 'Sagar', 'Dewas'],
    'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Navi Mumbai', 'Solapur', 'Kolhapur'],
    'Manipur': ['Imphal', 'Thoubal', 'Bishnupur'],
    'Meghalaya': ['Shillong', 'Tura', 'Jowai'],
    'Mizoram': ['Aizawl', 'Lunglei', 'Champhai'],
    'Nagaland': ['Kohima', 'Dimapur', 'Mokokchung'],
    'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Sambalpur', 'Puri'],
    'Punjab': ['Chandigarh', 'Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Mohali'],
    'Rajasthan': ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bikaner', 'Alwar', 'Bhilwara'],
    'Sikkim': ['Gangtok', 'Namchi', 'Pelling'],
    'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Erode', 'Vellore'],
    'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Khammam', 'Secunderabad'],
    'Tripura': ['Agartala', 'Udaipur', 'Dharmanagar'],
    'Uttar Pradesh': ['Lucknow', 'Noida', 'Ghaziabad', 'Agra', 'Varanasi', 'Kanpur', 'Prayagraj', 'Meerut', 'Greater Noida'],
    'Uttarakhand': ['Dehradun', 'Haridwar', 'Rishikesh', 'Haldwani', 'Roorkee', 'Nainital'],
    'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Siliguri', 'Asansol', 'Kharagpur']
  };

  const handleSocietyRequest = async (e) => {
    e.preventDefault();
    if (!requestForm.name || !requestForm.phone || !requestForm.society_name || !requestForm.state || !requestForm.city) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...requestForm,
        society_address: `${requestForm.city}, ${requestForm.state}${requestForm.society_address ? ' — ' + requestForm.society_address : ''}`
      };
      await axios.post(`${API}/society-request`, payload);
      setSubmitted(true);
      toast.success('Request submitted! We will get in touch soon.');
      setRequestForm({ name: '', phone: '', email: '', society_name: '', state: '', city: '', society_address: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally { setSubmitting(false); }
  };

  const roles = [
    {
      id: 'owner',
      title: 'Parking Owner',
      description: 'Monetize your vacant parking spot',
      icon: Car,
      color: '#09090B',
      testId: 'owner-role-card'
    },
    {
      id: 'guard',
      title: 'Security Guard',
      description: 'Manage visitor parking & check-ins',
      icon: Shield,
      color: '#09090B',
      testId: 'guard-role-card'
    },
    {
      id: 'admin',
      title: 'Admin',
      description: 'Analytics & society management',
      icon: BarChart3,
      color: '#09090B',
      testId: 'admin-role-card'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Hero Section */}
      <div 
        className="relative h-[45vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/b70eba2e-88eb-4f5d-9ebc-ce80d62e76ef/images/f79b311aade1fbb5b4cc974422e77eca7438edd41b46e5f351e6d784d292242f.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(9, 9, 11, 0.7)' }}></div>
        
        <div className="relative z-10 text-center px-6">
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black mb-6"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#FFFFFF' }}
            data-testid="landing-hero-title"
          >
            ParkShare
          </h1>
          <p 
            className="text-base sm:text-lg max-w-4xl mx-auto leading-relaxed whitespace-nowrap"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#F4F4F5' }}
            data-testid="landing-hero-subtitle"
          >
            Turn empty parking spots into earnings. Visitors check in with a scan — guard-assisted or completely self-service.
          </p>
        </div>
      </div>

      {/* Role Selection */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <p 
            className="text-xs tracking-[0.2em] uppercase font-bold mb-3"
            style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            Choose Your Role
          </p>
          <h2 
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
          >
            Get Started Now
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {roles.map((role) => {
            const IconComponent = role.icon;
            return (
              <div
                key={role.id}
                data-testid={role.testId}
                className="border p-8 transition-transform hover:-translate-y-1 cursor-pointer"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderColor: '#E4E4E7',
                  borderWidth: '1px'
                }}
                onClick={() => navigate(`/auth/${role.id}`)}
              >
                <div 
                  className="w-14 h-14 flex items-center justify-center mb-6"
                  style={{
                    backgroundColor: role.color,
                    ...(role.id === 'visitor' && { backgroundColor: '#FBBF24' })
                  }}
                >
                  <IconComponent 
                    size={28} 
                    style={{ color: role.id === 'visitor' ? '#09090B' : '#FFFFFF' }} 
                  />
                </div>
                
                <h3 
                  className="text-xl sm:text-2xl tracking-tight font-semibold mb-3"
                  style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
                >
                  {role.title}
                </h3>
                
                <p 
                  className="text-base leading-relaxed"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}
                >
                  {role.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16" style={{ backgroundColor: '#F4F4F5' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
            >
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div 
                className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: '#FBBF24' }}
              >
                <span 
                  className="text-2xl font-black"
                  style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}
                >
                  1
                </span>
              </div>
              <h3 
                className="text-xl font-semibold mb-2"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
              >
                Owner Marks Available
              </h3>
              <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                One-click toggle with return time (2-hour buffer auto-applied)
              </p>
            </div>

            <div className="text-center">
              <div 
                className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: '#FBBF24' }}
              >
                <span 
                  className="text-2xl font-black"
                  style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}
                >
                  2
                </span>
              </div>
              <h3 
                className="text-xl font-semibold mb-2"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
              >
                Guard Check-In
              </h3>
              <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                30-second vehicle entry with UPI QR generation
              </p>
            </div>

            <div className="text-center">
              <div 
                className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: '#FBBF24' }}
              >
                <span 
                  className="text-2xl font-black"
                  style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}
                >
                  3
                </span>
              </div>
              <h3 
                className="text-xl font-semibold mb-2"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
              >
                Visitor Parks
              </h3>
              <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                Digital pass with slot number and map direction
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Society Request Section */}
      <div className="py-20" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left — Copy */}
            <div>
              <p
                className="text-xs tracking-[0.2em] uppercase font-bold mb-3"
                style={{ color: '#FBBF24', fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Not on ParkShare yet?
              </p>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-6"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
              >
                Bring ParkShare to<br />Your Society
              </h2>
              <p
                className="text-base leading-relaxed mb-8"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}
              >
                If your society is not yet registered on ParkShare, let us know.
                Fill in the details and our team will reach out to get your society onboarded — no cost, no hassle.
              </p>

              <div className="space-y-5">
                {[
                  { num: '01', text: 'Submit your society details below' },
                  { num: '02', text: 'Our team verifies and sets up your society' },
                  { num: '03', text: 'Your admin gets access, residents get invited' },
                ].map((step) => (
                  <div key={step.num} className="flex items-start gap-4">
                    <span
                      className="text-xs font-black px-2 py-1 shrink-0"
                      style={{ backgroundColor: '#09090B', color: '#FBBF24', fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {step.num}
                    </span>
                    <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>
                      {step.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Form */}
            <div>
              {submitted ? (
                <div
                  className="border-2 p-10 text-center"
                  style={{ borderColor: '#10B981', backgroundColor: '#F0FDF4' }}
                  data-testid="request-success"
                >
                  <div
                    className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: '#10B981' }}
                  >
                    <Send size={28} style={{ color: '#FFFFFF' }} />
                  </div>
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
                  >
                    Request Received
                  </h3>
                  <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                    We'll get in touch within 24 hours to onboard your society.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-sm mt-4 underline"
                    style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSocietyRequest}
                  className="border p-8"
                  style={{ borderColor: '#E4E4E7', backgroundColor: '#FAFAFA' }}
                  data-testid="society-request-form"
                >
                  <h3
                    className="text-lg font-bold mb-6"
                    style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
                  >
                    Request Your Society
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>
                        <User size={14} style={{ color: '#71717A' }} /> Your Name *
                      </label>
                      <Input
                        value={requestForm.name}
                        onChange={(e) => setRequestForm({ ...requestForm, name: e.target.value })}
                        required
                        data-testid="request-name"
                        className="border-2 focus:border-black"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>
                          <Phone size={14} style={{ color: '#71717A' }} /> Contact Number *
                        </label>
                        <Input
                          value={requestForm.phone}
                          onChange={(e) => setRequestForm({ ...requestForm, phone: e.target.value })}
                          required
                          data-testid="request-phone"
                          className="border-2 focus:border-black"
                          style={{ fontFamily: 'JetBrains Mono, monospace' }}
                        />
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>
                          <Mail size={14} style={{ color: '#71717A' }} /> Email
                        </label>
                        <Input
                          type="email"
                          value={requestForm.email}
                          onChange={(e) => setRequestForm({ ...requestForm, email: e.target.value })}
                          data-testid="request-email"
                          className="border-2 focus:border-black"
                        />
                      </div>
                    </div>

                    <div className="pt-2 mt-2" style={{ borderTop: '1px solid #E4E4E7' }}>
                      <label className="flex items-center gap-2 text-sm font-medium mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>
                        <Building2 size={14} style={{ color: '#71717A' }} /> Society Name *
                      </label>
                      <Input
                        value={requestForm.society_name}
                        onChange={(e) => setRequestForm({ ...requestForm, society_name: e.target.value })}
                        required
                        data-testid="request-society-name"
                        className="border-2 focus:border-black"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>
                          <MapPin size={14} style={{ color: '#71717A' }} /> State *
                        </label>
                        <select
                          value={requestForm.state}
                          onChange={(e) => setRequestForm({ ...requestForm, state: e.target.value, city: '' })}
                          required
                          data-testid="request-state"
                          className="w-full h-10 px-3 border-2 text-sm focus:border-black focus:outline-none"
                          style={{ fontFamily: 'IBM Plex Sans, sans-serif', borderColor: '#E4E4E7', backgroundColor: '#FFFFFF', color: requestForm.state ? '#09090B' : '#A1A1AA' }}
                        >
                          <option value="" disabled>Select State</option>
                          {Object.keys(indianStates).sort().map(state => (
                            <option key={state} value={state} style={{ color: '#09090B' }}>{state}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>
                          <MapPin size={14} style={{ color: '#71717A' }} /> City *
                        </label>
                        <select
                          value={requestForm.city}
                          onChange={(e) => setRequestForm({ ...requestForm, city: e.target.value })}
                          required
                          disabled={!requestForm.state}
                          data-testid="request-city"
                          className="w-full h-10 px-3 border-2 text-sm focus:border-black focus:outline-none disabled:opacity-50"
                          style={{ fontFamily: 'IBM Plex Sans, sans-serif', borderColor: '#E4E4E7', backgroundColor: '#FFFFFF', color: requestForm.city ? '#09090B' : '#A1A1AA' }}
                        >
                          <option value="" disabled>{requestForm.state ? 'Select City' : 'Select state first'}</option>
                          {(indianStates[requestForm.state] || []).map(city => (
                            <option key={city} value={city} style={{ color: '#09090B' }}>{city}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>
                        <MapPin size={14} style={{ color: '#71717A' }} /> Locality / Area
                      </label>
                      <Input
                        value={requestForm.society_address}
                        onChange={(e) => setRequestForm({ ...requestForm, society_address: e.target.value })}
                        data-testid="request-society-address"
                        className="border-2 focus:border-black"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full mt-6 p-4"
                    style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}
                    data-testid="submit-society-request"
                  >
                    {submitting ? 'Submitting...' : 'Request Onboarding'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
